/**
 * Shared Dashboard handlers — DRYs up logic duplicated between
 * Dashboard.web.tsx and Dashboard.mobile.tsx.
 */

import { useState, useCallback, useRef } from "react";
import { updateSettings } from "../services/settingsService";
import { toastPatterns } from "../utils/toastPatterns";
import type { useWhatIfScenario } from "./useWhatIfScenario";

type WhatIfState = ReturnType<typeof useWhatIfScenario>;

interface UseDashboardHandlersArgs {
  userId: string | undefined;
  whatIf: WhatIfState;
  loadScenario: (id: string) => Promise<boolean>;
  saveScenario: (name: string, description: string) => Promise<boolean>;
}

export function useDashboardHandlers({
  userId,
  whatIf,
  loadScenario,
  saveScenario,
}: UseDashboardHandlersArgs) {
  // ── What-If save ────────────────────────────────────────────────
  const handleWhatIfSave = useCallback(async () => {
    if (!userId || !whatIf.isActive || !whatIf.sliderValues) return;
    try {
      await updateSettings(userId, "assumptions", {
        retirementAge: whatIf.sliderValues.retirementAge,
        lifeExpectancy: whatIf.sliderValues.lifeExpectancy,
        expectedReturnRate: whatIf.sliderValues.expectedReturnRate,
        inflationRate: whatIf.sliderValues.inflationRate,
        expensesAtRetirement: whatIf.sliderValues.expensesAtRetirement,
      });
      whatIf.reset();
      toastPatterns.success("Settings saved.");
    } catch {
      toastPatterns.error(
        "Failed to save settings. Please check your connection and try again."
      );
    }
  }, [userId, whatIf]);

  // ── Scenario comparison ─────────────────────────────────────────
  const [comparisonScenarioIds, setComparisonScenarioIds] = useState<
    string[] | null
  >(null);
  const savedScrollRef = useRef(0);

  const openComparison = useCallback((ids: string[]) => {
    savedScrollRef.current = window.scrollY;
    setComparisonScenarioIds(ids);
  }, []);

  const closeComparison = useCallback(() => {
    setComparisonScenarioIds(null);
    requestAnimationFrame(() => {
      window.scrollTo(0, savedScrollRef.current);
    });
  }, []);

  const handleCompareViewDetails = useCallback(
    async (scenarioId: string) => {
      await loadScenario(scenarioId);
      closeComparison();
    },
    [loadScenario, closeComparison]
  );

  // ── Save scenario ───────────────────────────────────────────────
  const handleSaveScenario = useCallback(
    async (input: { name: string; description: string }) => {
      await saveScenario(input.name, input.description);
    },
    [saveScenario]
  );

  return {
    handleWhatIfSave,
    comparisonScenarioIds,
    openComparison,
    closeComparison,
    handleCompareViewDetails,
    handleSaveScenario,
  };
}
